// Try-on — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.5–4.7)
//
// Three phases driven by a single `phase` state:
//   • start      — two stacked slots (You + Item) + Generate
//   • generating — 3-step progress checklist over a shimmering 3:4 card
//   • result     — hero image + Save/Try-again/Share + optional video CTA
//
// Back-photo / 360° / PhotoTips intentionally dropped (see CLAUDE.md §12).
// Video clip kept as a secondary "See it move" CTA on the result screen.

import { useState, useRef, useEffect } from 'react';
import api from './api';
import CameraCapture from './CameraCapture';
import Header from './components/Header';
import { resolveUrl } from './utils/media';

const POLL_INTERVAL = 5000;
const STORAGE_KEY   = 'trimie_tryon_session';
const ENGINE_KEY    = 'trimie_tryon_engine';

// Two generation engines, selectable per try-on (backend dispatches on the
// `provider` form field). Kling stays the default; FASHN is the fast engine
// and the only one that renders standalone tops/bottoms correctly.
const ENGINES = [
  { id: 'kling', name: 'Kling', blurb: 'Best for dresses · ~60s', eta: '30–60 seconds' },
  { id: 'fashn', name: 'FASHN', blurb: 'Any garment · ~10s',      eta: '5–15 seconds' },
];

const friendlyKlingError = (raw) => {
  if (!raw) return 'Something went wrong. Try a different photo.';
  const s = String(raw).toLowerCase();
  // Garment-side failures — Kling rejected the dress_input image. Common cause
  // is a worn shot or cluttered scene where its detector can't isolate the
  // garment. Steer the user to a flat-lay product photo.
  if (s.includes('no garment detected') || s.includes('clothes image upload failed') ||
      s.includes('upload dress image') || s.includes('garment')) {
    return (
      "Kling couldn't find the garment in that photo. Use a flat-lay or product-shot " +
      "of the item alone — plain background, the whole piece in frame, no person wearing it."
    );
  }
  // Model-side failures (body photo problems). Kling's most common 10000 also
  // covers pose issues — only fall into this branch after the garment branch.
  if (s.includes('500') || s.includes('common response') || s.includes('code 10000'))
    return "Kling couldn't process the body photo. Use one with a plain background, arms at your sides, full body in frame.";
  if (s.includes('balance') || s.includes('insufficient') || s.includes('credit'))
    return 'Out of Kling credits. Top up your PiAPI balance to continue.';
  if (s.includes('timeout')) return 'Kling took too long. Try again in a minute.';
  return String(raw);
};

const friendlyFashnError = (raw) => {
  if (!raw) return 'Something went wrong. Try a different photo.';
  const s = String(raw).toLowerCase();
  if (s.includes('moderation') || s.includes('nsfw'))
    return 'FASHN flagged the photo. Use a regular clothed body photo and a standalone garment shot.';
  if (s.includes('pose'))
    return "FASHN couldn't read the pose. Use a front-facing, full-body photo with arms at your sides.";
  if (s.includes('credit') || s.includes('insufficient') || s.includes('balance'))
    return 'Out of FASHN credits. Top up at app.fashn.ai to continue.';
  if (s.includes('load') && s.includes('image'))
    return "FASHN couldn't load one of the images. Re-upload as JPG or PNG and try again.";
  if (s.includes('cancel')) return 'The try-on was canceled. Hit Generate to run it again.';
  return String(raw);
};

const validatePhoto = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const short = Math.min(img.naturalWidth, img.naturalHeight);
    const long  = Math.max(img.naturalWidth, img.naturalHeight);
    if (short < 512) resolve(`Photo is too small (${short}px). Shorter side must be ≥512px.`);
    else if (long > 4096) resolve(`Photo is too large (${long}px). Longer side must be ≤4096px.`);
    else resolve(null);
  };
  img.onerror = () => resolve('Could not read that file. Use JPG or PNG.');
  img.src = URL.createObjectURL(file);
});

// ── Generating: 3-step checklist ────────────────────────────────────────────
const STEPS = ['Analyzing photo', 'Detecting body shape', 'Compositing garment'];

function GeneratingPhase({ engineName, statusText, elapsed, eta }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Steps tick at 0s, 6s, 12s
  const activeIdx = Math.min(2, Math.floor(tick / 6));
  return (
    <div className="tryon-gen">
      <div className="tryon-gen-card">
        <div className="tryon-gen-shimmer" />
        <div className="tryon-gen-dot" />
      </div>
      <ol className="tryon-gen-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending'}>
            <span className="tryon-gen-mark">
              {i < activeIdx ? '✓' : i === activeIdx ? <span className="tryon-gen-pulse" /> : '○'}
            </span>
            {s}
          </li>
        ))}
      </ol>
      <p className="tryon-gen-tip">
        {statusText
          ? <>{engineName}: <strong>{statusText}</strong> · {elapsed}s elapsed</>
          : `Usually takes ${eta}.`}
      </p>
    </div>
  );
}

// ── Photo tips — collapsible card with body-photo requirements ──────────────
// Brought back by request after Session 3 cleanup. Kept inline (no separate
// PhotoTips.js file) so it travels with the screen it serves.
const PHOTO_TIPS = [
  { title: 'Full body in frame',  body: 'Head to feet, no cropping.' },
  { title: 'Face forward',        body: 'Look at the camera, stand straight.' },
  { title: 'Arms close to body',  body: "Don't spread arms, don't hold things." },
  { title: 'Simple background',   body: 'Plain wall or daylight outdoors works best.' },
  { title: 'Tight or fitted clothes', body: 'Layers and bulky jackets confuse the AI.' },
  { title: 'JPG or PNG, ≥ 512px', body: 'Webp + huge files get rejected by the engines.' },
];

function PhotoTips({ open, onToggle }) {
  return (
    <div className={`tryon-tips${open ? ' open' : ''}`}>
      <button type="button" className="tryon-tips-head" onClick={onToggle}>
        <span className="tryon-tips-title">For best results</span>
        <svg className="tryon-tips-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="tryon-tips-list">
          {PHOTO_TIPS.map((t) => (
            <li key={t.title} className="tryon-tips-item">
              <span className="tryon-tips-dot" />
              <div>
                <div className="tryon-tips-item-title">{t.title}</div>
                <div className="tryon-tips-item-body">{t.body}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Slot card (You + Item share this) ───────────────────────────────────────
function Slot({ label, hint, preview, onPick, onClear, badge }) {
  return (
    <button
      type="button"
      className={`tryon-slot${preview ? ' filled' : ''}`}
      onClick={onPick}
    >
      {preview ? (
        <>
          <img src={preview} alt={label} />
          <span className="tryon-slot-edit" onClick={(e) => { e.stopPropagation(); onClear(); }}>
            Change
          </span>
        </>
      ) : (
        <>
          <span className="mono-eyebrow">{label}</span>
          <span className="tryon-slot-hint">{hint}</span>
          {badge && <span className="tryon-slot-badge">{badge}</span>}
        </>
      )}
    </button>
  );
}

export default function TryOnPage({ onNav, savedModelUrl, navContext }) {
  // phase: 'start' | 'generating' | 'result'
  const [phase, setPhase] = useState('start');

  // model photo
  const [modelFile, setModelFile]       = useState(null);
  const [modelPreview, setModelPreview] = useState(null);
  const [showCam, setShowCam]           = useState(false);
  const [showModelSheet, setShowModelSheet] = useState(false);

  // garment
  const [garments, setGarments]               = useState([]);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const [garmentFile, setGarmentFile]         = useState(null);
  const [garmentLoading, setGarmentLoading]   = useState(false);
  const [showGarmentSheet, setShowGarmentSheet] = useState(false);
  const [importUrl, setImportUrl]             = useState('');
  const [importLoading, setImportLoading]     = useState(false);
  const [importError, setImportError]         = useState(null);

  // engine — remembered across sessions; Kling stays the default
  const [engine, setEngine] = useState(() => {
    try { return localStorage.getItem(ENGINE_KEY) === 'fashn' ? 'fashn' : 'kling'; }
    catch { return 'kling'; }
  });
  const pickEngine = (id) => {
    setEngine(id);
    try { localStorage.setItem(ENGINE_KEY, id); } catch { /* quota */ }
  };
  const engineMeta = ENGINES.find(e => e.id === engine) || ENGINES[0];

  // submission/polling
  const [taskId, setTaskId]         = useState(null);
  const [resultUrl, setResultUrl]   = useState(null);
  const [errorMsg, setErrorMsg]     = useState(null);

  // video
  const [videoStep, setVideoStep] = useState('idle'); // idle|polling|done|error
  const [videoUrl, setVideoUrl]   = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null); // raw Kling status: pending|processing|staged|...
  const [videoElapsed, setVideoElapsed] = useState(0);
  const videoElapsedRef = useRef(null);
  const VIDEO_TIMEOUT_MS = 3 * 60 * 1000;

  // image generation status display (during phase === 'generating')
  const [imageStatus, setImageStatus]   = useState(null);
  const [imageElapsed, setImageElapsed] = useState(0);
  const imageElapsedRef = useRef(null);
  const IMAGE_TIMEOUT_MS = 5 * 60 * 1000;

  const [photoError, setPhotoError] = useState(null);
  // Photo tips — open by default the first time so the requirements are
  // visible; user can collapse and we remember the preference.
  const [tipsOpen, setTipsOpen] = useState(() => {
    try { return localStorage.getItem('trimie_tips_collapsed') !== '1'; }
    catch { return true; }
  });
  const toggleTips = () => {
    setTipsOpen((open) => {
      try { localStorage.setItem('trimie_tips_collapsed', open ? '1' : '0'); }
      catch { /* quota */ }
      return !open;
    });
  };

  const pollRef       = useRef(null);
  const videoPollRef  = useRef(null);
  const modelInputRef = useRef(null);
  const garmentInputRef = useRef(null);

  // ── mount: load closet + restore session + preload model photo ─────────────
  useEffect(() => {
    api.get('/tryon/upload-row-clothes/')
      .then(res => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        setGarments(Array.isArray(data) ? data : []);
      })
      .catch(() => setGarments([]));

    if (!navContext?.modelPhotoUrl) {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved?.taskId && saved?.resultUrl) {
          setTaskId(saved.taskId);
          setResultUrl(saved.resultUrl);
          if (saved.modelPreview) setModelPreview(saved.modelPreview);
          if (saved.videoUrl) { setVideoUrl(saved.videoUrl); setVideoStep('done'); }
          setPhase('result');
        }
      } catch { /* corrupt */ }
    }

    const preloadUrl = navContext?.modelPhotoUrl || savedModelUrl;
    if (preloadUrl) {
      const resolved = resolveUrl(preloadUrl);
      setModelPreview(resolved);
      fetch(resolved)
        .then(r => r.blob())
        .then(blob => setModelFile(new File([blob], 'model.jpg', { type: blob.type || 'image/jpeg' })))
        .catch(() => {});
    }

    return () => {
      clearInterval(pollRef.current);
      clearInterval(videoPollRef.current);
      clearInterval(videoElapsedRef.current);
      clearInterval(imageElapsedRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── persist result session ────────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'result' && taskId && resultUrl) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          taskId, resultUrl, modelPreview, videoUrl: videoUrl || null,
        }));
      } catch { /* quota */ }
    }
  }, [phase, taskId, resultUrl, videoUrl, modelPreview]);

  // ── photo helpers ─────────────────────────────────────────────────────────
  const applyModel = async (f) => {
    const err = await validatePhoto(f);
    if (err) { setPhotoError(err); return; }
    setPhotoError(null);
    setModelFile(f);
    setModelPreview(URL.createObjectURL(f));
    setShowModelSheet(false);
  };

  const handleSelectGarment = async (g) => {
    setSelectedGarment(g);
    setGarmentFile(null);
    setGarmentLoading(true);
    setShowGarmentSheet(false);
    try {
      const url  = resolveUrl(g.input_url || g.image);
      const blob = await (await fetch(url)).blob();
      setGarmentFile(new File([blob], 'garment.jpg', { type: blob.type || 'image/jpeg' }));
    } catch { setSelectedGarment(null); }
    finally  { setGarmentLoading(false); }
  };

  const handleManualGarment = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setGarmentFile(f);
    setSelectedGarment({ id: '__manual__', label: f.name, preview: URL.createObjectURL(f) });
    setShowGarmentSheet(false);
  };

  const handleImportUrl = async (e) => {
    e.preventDefault();
    const url = importUrl.trim();
    if (!url) return;
    setImportLoading(true);
    setImportError(null);
    try {
      const res = await api.post('/tryon/import-garment/', { url });
      const saved = res.data?.data;
      if (saved) {
        setGarments(prev => [saved, ...prev]);
        setImportUrl('');
        handleSelectGarment(saved);
      }
    } catch (err) {
      setImportError(err.response?.data?.message || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  // ── generate ──────────────────────────────────────────────────────────────
  const stopImageTimers = () => {
    clearInterval(pollRef.current);
    clearInterval(imageElapsedRef.current);
  };

  const handleGenerate = async () => {
    if (!modelFile || !garmentFile) return;
    setErrorMsg(null);
    setResultUrl(null);
    setVideoUrl(null);
    setVideoStep('idle');
    setImageStatus('submitting');
    setImageElapsed(0);
    setPhase('generating');

    const startedAt = Date.now();
    imageElapsedRef.current = setInterval(() => {
      setImageElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    // Kling's current pipeline always slots a single garment into dress_input.
    // We still send garment_category so the backend can reject unsupported
    // types early (shoes/bags) and so future routing (upper+lower pair) can
    // read it without another schema change.
    const garmentCategory = selectedGarment?.category || 'other';
    const form = new FormData();
    form.append('model_input', modelFile);
    form.append('dress_input', garmentFile);
    form.append('garment_category', garmentCategory);
    form.append('provider', engine);
    try {
      const res = await api.post('/tryon/image/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const id = res.data?.data?.task_id;
      if (!id) throw new Error('No task ID returned.');
      setTaskId(id);
      startPoll(id, startedAt);
    } catch (err) {
      stopImageTimers();
      setErrorMsg(err.response?.data?.message || err.message || 'Submission failed.');
      setPhase('start');
    }
  };

  const startPoll = (id, startedAt) => {
    pollRef.current = setInterval(async () => {
      // Hard 5-min cap — if Kling hasn't moved past pending by then, it's
      // queued on a credit-starved account or PiAPI is jammed.
      if (Date.now() - startedAt > IMAGE_TIMEOUT_MS) {
        stopImageTimers();
        setErrorMsg(`Try-on took longer than 5 minutes. ${engineMeta.name} may be queued or out of credits.`);
        setPhase('start');
        return;
      }
      try {
        const res = await api.get(`/tryon/image/${id}/`);
        const st  = res.data?.data?.data?.status || res.data?.data?.status;
        setImageStatus(st);
        if (st === 'completed') {
          stopImageTimers();
          const url = res.data?.data?.tryon_image_url;
          if (url) setResultUrl(resolveUrl(url));
          setPhase('result');
        } else if (st === 'failed') {
          stopImageTimers();
          const raw = res.data?.data;
          const errObj = raw?.data?.error;
          const detail =
            raw?.data?.error_detail ||
            (errObj && typeof errObj === 'object'
              ? (errObj.raw_message || errObj.message)
              : errObj) ||
            raw?.error_detail ||
            raw?.error_message ||
            `${engineMeta.name} reported failure.`;
          const friendly = engine === 'fashn' ? friendlyFashnError : friendlyKlingError;
          setErrorMsg(friendly(typeof detail === 'string' ? detail : null));
          setPhase('start');
        }
      } catch (err) {
        stopImageTimers();
        setErrorMsg('Polling error: ' + (err.message || 'unknown'));
        setPhase('start');
      }
    }, POLL_INTERVAL);
  };

  const handleCancelGenerating = () => {
    stopImageTimers();
    setPhase('start');
    setTaskId(null);
  };

  // ── video ─────────────────────────────────────────────────────────────────
  const stopVideoTimers = () => {
    clearInterval(videoPollRef.current);
    clearInterval(videoElapsedRef.current);
  };

  const handleGenerateVideo = async () => {
    if (!taskId) return;
    setVideoStep('polling');
    setVideoError(null);
    setVideoStatus('submitting');
    setVideoElapsed(0);
    const startedAt = Date.now();
    videoElapsedRef.current = setInterval(() => {
      setVideoElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    try {
      const res = await api.post('/tryon/video/', { tryon_id: taskId });
      const id  = res.data?.data?.task_id || res.data?.data?.video_task_id;
      if (!id) throw new Error('No video task ID');
      videoPollRef.current = setInterval(async () => {
        // Hard timeout — Kling is stuck if it hasn't moved past pending in 3 min.
        if (Date.now() - startedAt > VIDEO_TIMEOUT_MS) {
          stopVideoTimers();
          setVideoError('Video took longer than 3 minutes. Kling may be queued or out of credits.');
          setVideoStep('error');
          return;
        }
        try {
          const r   = await api.get(`/tryon/video/${id}/`);
          const st  = r.data?.data?.status;
          const url = r.data?.data?.stored_video_url;
          setVideoStatus(st);
          if (st === 'completed') {
            stopVideoTimers();
            if (url) {
              setVideoUrl(resolveUrl(url));
              setVideoStep('done');
            } else {
              setVideoError('Video finished but no URL was returned.');
              setVideoStep('error');
            }
          } else if (st === 'failed') {
            stopVideoTimers();
            setVideoError(r.data?.data?.error_detail || 'Video failed.');
            setVideoStep('error');
          }
        } catch {
          stopVideoTimers();
          setVideoStep('error');
        }
      }, POLL_INTERVAL);
    } catch {
      stopVideoTimers();
      setVideoStep('error');
    }
  };

  const handleCancelVideo = () => {
    stopVideoTimers();
    setVideoStep('idle');
    setVideoStatus(null);
    setVideoElapsed(0);
  };

  // ── reset / try again ─────────────────────────────────────────────────────
  const handleTryAgain = () => {
    clearInterval(pollRef.current);
    clearInterval(videoPollRef.current);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setModelFile(null); setModelPreview(null);
    setGarmentFile(null); setSelectedGarment(null);
    setTaskId(null); setResultUrl(null); setErrorMsg(null);
    setVideoStep('idle'); setVideoUrl(null); setVideoError(null);
    setPhase('start');
  };

  // Native share. Prefer the video when it's ready — that's the more
  // shareable artefact. Falls back to image, then to clipboard if the
  // browser has no Web Share API.
  const handleShare = async () => {
    const url   = (videoStep === 'done' && videoUrl) ? videoUrl : resultUrl;
    const title = (videoStep === 'done' && videoUrl) ? 'My TRIMIE video' : 'My TRIMIE look';
    if (!url) return;
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
    }
  };

  const garmentCategory = selectedGarment?.category || null;
  const garmentIsUnsupported = garmentCategory === 'shoes' || garmentCategory === 'bags';
  const garmentIsImperfect =
    garmentCategory && garmentCategory !== 'dresses' && garmentCategory !== 'other' && !garmentIsUnsupported;

  const canGenerate = modelFile && garmentFile && !garmentLoading && !garmentIsUnsupported;
  const garmentPreview = selectedGarment?.preview || (selectedGarment ? resolveUrl(selectedGarment.input_url || selectedGarment.image) : null);

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="screen tryon-screen">
      {showCam && (
        <CameraCapture
          label="Take a body photo"
          onCapture={(f) => { applyModel(f); setShowCam(false); }}
          onClose={() => setShowCam(false)}
        />
      )}

      <Header
        title="Try-on"
        onBack={phase === 'start' ? () => onNav('home') : null}
        action={phase === 'generating'
          ? { icon: <span>Cancel</span>, onClick: handleCancelGenerating, ariaLabel: 'Cancel' }
          : phase === 'result'
            ? { icon: ShareIcon, onClick: handleShare, ariaLabel: 'Share' }
            : { icon: <span>?</span>, onClick: () => setTipsOpen(true), ariaLabel: 'Photo tips' }}
      />

      {/* hidden file inputs */}
      <input ref={modelInputRef} type="file" accept="image/jpeg,image/png" hidden
             onChange={(e) => { const f = e.target.files[0]; if (f) applyModel(f); }} />
      <input ref={garmentInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
             onChange={handleManualGarment} />

      {/* ── START ──────────────────────────────────────────────────────── */}
      {phase === 'start' && (
        <div className="tryon-start">
          <Slot
            label="You"
            hint="Tap to add a body photo"
            preview={modelPreview}
            onPick={() => setShowModelSheet(true)}
            onClear={() => { setModelFile(null); setModelPreview(null); }}
          />
          <Slot
            label="Item"
            hint="Tap to pick from closet or paste a link"
            preview={garmentPreview}
            badge={garmentLoading ? 'Loading…' : null}
            onPick={() => setShowGarmentSheet(true)}
            onClear={() => { setGarmentFile(null); setSelectedGarment(null); }}
          />

          <div className="tryon-engine">
            <span className="mono-eyebrow">Engine</span>
            <div className="tryon-engine-row">
              {ENGINES.map(e => (
                <button
                  key={e.id}
                  type="button"
                  className={`cat-chip${engine === e.id ? ' active' : ''}`}
                  onClick={() => pickEngine(e.id)}
                >
                  {e.name}
                  <span className="tryon-engine-blurb">{e.blurb}</span>
                </button>
              ))}
            </div>
          </div>

          {garmentIsUnsupported && (
            <div className="alert error" style={{ marginTop: -4 }}>
              Try-on doesn't support {garmentCategory} yet. Pick a top, bottom, or full-piece outfit.
            </div>
          )}
          {garmentIsImperfect && engine === 'kling' && (
            <div className="tryon-hint">
              Heads up — Kling currently works best with dresses and one-piece outfits.
              {garmentCategory === 'tops' && ' A standalone top may render as a full dress.'}
              {garmentCategory === 'bottoms' && ' Standalone bottoms may not render accurately.'}
              {garmentCategory === 'outerwear' && ' Open jackets may render closed or full-length.'}
              {' '}Switch to FASHN for single tops or bottoms.
            </div>
          )}
          {!garmentFile && !selectedGarment && (
            <div className="tryon-hint">
              <strong>Garment photo tips:</strong> flat-lay or product shot of the item alone,
              plain background, whole piece in frame.
              {engine === 'kling'
                ? ' Avoid worn shots and busy scenes — Kling rejects them with "no garment detected".'
                : ' FASHN also accepts photos of the item worn on someone.'}
            </div>
          )}

          <PhotoTips open={tipsOpen} onToggle={toggleTips} />

          {photoError && <div className="alert error">{photoError}</div>}
          {errorMsg && <div className="alert error">{errorMsg}</div>}

          <div style={{ flex: 1 }} />

          <button
            className="btn-purple btn-lg"
            onClick={handleGenerate}
            disabled={!canGenerate}
          >
            Generate try-on
          </button>
        </div>
      )}

      {/* ── GENERATING ─────────────────────────────────────────────────── */}
      {phase === 'generating' && (
        <GeneratingPhase
          engineName={engineMeta.name}
          statusText={imageStatus}
          elapsed={imageElapsed}
          eta={engineMeta.eta}
        />
      )}

      {/* ── RESULT ─────────────────────────────────────────────────────── */}
      {phase === 'result' && (
        <div className="tryon-result">
          <div className="tryon-result-hero">
            {resultUrl
              ? <img src={resultUrl} alt="Your try-on result" />
              : <div className="tryon-result-empty" />}
          </div>

          <div className="tryon-result-actions">
            <button className="btn-outline" onClick={handleTryAgain}>↻ Try again</button>
            {videoStep === 'idle' && (
              <button className="btn-purple" onClick={handleGenerateVideo}>See it move</button>
            )}
            {videoStep === 'polling' && (
              <button className="btn-purple" disabled>
                <span className="spinner" style={{ marginRight: 6 }} /> {videoElapsed}s
              </button>
            )}
            {videoStep === 'done' && (
              <button className="btn-purple" disabled>✓ Video ready</button>
            )}
            {videoStep === 'error' && (
              <button className="btn-outline" onClick={handleGenerateVideo}>Retry video</button>
            )}
          </div>

          {videoStep === 'polling' && (
            <div className="tryon-status">
              <span className="spinner" />
              <span>
                Kling: <strong>{videoStatus || 'submitting'}</strong> · {videoElapsed}s elapsed
                {' '}
                <button type="button" className="link-inline" onClick={handleCancelVideo}>Cancel</button>
              </span>
            </div>
          )}

          {videoError && <div className="alert error">{videoError}</div>}

          {videoUrl && (
            <div className="tryon-result-video">
              <video src={videoUrl} autoPlay loop playsInline controls />
            </div>
          )}
        </div>
      )}

      {/* ── Model photo sheet (Camera / Library) ───────────────────────── */}
      {showModelSheet && (
        <BottomSheet title="Body photo" onClose={() => setShowModelSheet(false)}>
          <button className="btn-outline btn-lg" onClick={() => { setShowModelSheet(false); setShowCam(true); }}>
            Camera
          </button>
          <button className="btn-outline btn-lg" onClick={() => { setShowModelSheet(false); modelInputRef.current?.click(); }}>
            Library
          </button>
        </BottomSheet>
      )}

      {/* ── Garment picker sheet ───────────────────────────────────────── */}
      {showGarmentSheet && (
        <BottomSheet title="Pick an item" onClose={() => setShowGarmentSheet(false)}>
          {garments.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--t-muted)', margin: 0 }}>
              Your closet is empty.{' '}
              <button type="button" className="link-inline" onClick={() => onNav('add')}>Add an item</button>{' '}
              to try it on.
            </p>
          ) : (
            <div className="tryon-garment-row">
              {garments.map(g => {
                const url = resolveUrl(g.input_url || g.image);
                return (
                  <button key={g.id} type="button"
                          className={`tryon-garment-thumb${selectedGarment?.id === g.id ? ' active' : ''}`}
                          onClick={() => handleSelectGarment(g)}>
                    {url
                      ? <img src={url} alt={g.label || 'garment'} />
                      : <div className="tryon-garment-empty" />}
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={handleImportUrl} style={{ display: 'flex', gap: 8 }}>
            <input
              type="url"
              placeholder="Paste a product link…"
              value={importUrl}
              onChange={(e) => { setImportUrl(e.target.value); setImportError(null); }}
              style={{ flex: 1 }}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="btn-purple" disabled={importLoading || !importUrl.trim()}>
              {importLoading ? '…' : 'Import'}
            </button>
          </form>
          {importError && <div className="alert error">{importError}</div>}

          <button className="btn-outline" onClick={() => { setShowGarmentSheet(false); garmentInputRef.current?.click(); }}>
            Upload image instead
          </button>
        </BottomSheet>
      )}
    </div>
  );
}

// ── Bottom sheet primitive ─────────────────────────────────────────────────
function BottomSheet({ title, children, onClose }) {
  return (
    <div className="t-sheet-backdrop" onClick={onClose}>
      <div className="t-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="t-sheet-handle" />
        <div className="t-sheet-title">{title}</div>
        <div className="t-sheet-body">{children}</div>
      </div>
    </div>
  );
}

const ShareIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);
